import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Logo from "@/components/common/Logo";
import { APP_VERSION } from "@/version";

const HERO_URL = "https://images.unsplash.com/photo-1580014317999-e9f1936787a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhdXRvbW90aXZlJTIwZ2FyYWdlJTIwZGFyayUyMHR1bmluZ3xlbnwwfHx8fDE3ODIxNjMyNDB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user.role) {
      if (user.role === "admin") nav("/admin", { replace: true });
      else if (user.role === "stock_staff") nav("/stok", { replace: true });
      else nav("/b2b", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Giriş başarılı", { description: `Hoş geldiniz, ${data.name || data.email}` });
    } catch (err) {
      toast.error("Giriş başarısız", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (mail, pass) => {
    setEmail(mail); setPassword(pass);
    setLoading(true);
    try {
      const data = await login(mail, pass);
      toast.success("Giriş başarılı", { description: `Hoş geldiniz, ${data.name || data.email}` });
    } catch (err) {
      toast.error("Giriş başarısız", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-950">
      {/* Left hero */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO_URL} alt="Tuning garage" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <Logo size="lg" />
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl xl:text-5xl font-bold text-slate-50 leading-tight">
              Bayi Paneline <span className="text-red-500">Giriş</span>.
            </h1>
            <p className="mt-4 text-slate-300 text-base">
              Facelift setleri, tampon, far, stop, body kit ve tüm araçlar için aksesuarlar burada!
            </p>
            <div className="gradient-line mt-8" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Logo size="md" />
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Giriş</div>
          <h2 className="text-3xl font-bold text-slate-50">Hesabınıza giriş yapın</h2>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-widest">E-posta</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                className="mt-2 bg-slate-900 border-slate-800 text-slate-50 focus-visible:ring-red-500 focus-visible:border-red-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@sirket.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-widest">Şifre</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                className="mt-2 bg-slate-900 border-slate-800 text-slate-50 focus-visible:ring-red-500 focus-visible:border-red-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-red-600 hover:bg-red-500 text-white h-11 font-semibold uppercase tracking-widest text-sm"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>

          {/* Hızlı giriş bölümü kaldırıldı — production giriş bilgileri sadece admin panelinde */}

          <div className="gradient-line mt-10" />
          <p className="mt-6 text-xs text-slate-500 text-center">
            © {new Date().getFullYear()} HK Tuning. Sadece yetkili bayiler için.
          </p>
          <p className="mt-2 text-xs text-slate-500 text-center">
            Bu web sitesi{" "}
            <a
              href="https://www.dijitalroket.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dijital-roket-link"
              className="font-semibold bg-gradient-to-r from-orange-400 via-red-500 to-purple-500 bg-clip-text text-transparent hover:from-orange-300 hover:to-purple-400 transition-colors"
            >
              Dijital Roket
            </a>
            {" "}tarafından geliştirilmiştir.
            <span className="ml-2 text-slate-600" data-testid="app-version-login">v{APP_VERSION}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
